import { Component, Fragment } from 'react';
import * as Styles from './captcha.module.less';
import axios from 'axios';
import { CaptchaStore } from '../stores/captcha.store';
import { observer } from 'mobx-react';
import { injectDependency, provide } from '@servicetitan/react-ioc';
import { CaptchaApi } from '../api/captcha.api';
import { Link, Text } from '@servicetitan/design-system';
import classNames from 'classnames';

export interface CaptchaProps {
    forceShow: boolean;
    onVerified?: () => Promise<void>;
    callback?: (token: string) => void;
    onError?: (e: string) => void;
    onLoaded?: () => void;
    params?: {
        username?: string;
    };
}

@provide({
    singletons: [CaptchaApi],
})
@observer
export class Captcha extends Component<CaptchaProps> {
    @injectDependency(CaptchaStore)
    declare private captchaStore: CaptchaStore;

    private script?: HTMLScriptElement;
    private captchaListenerAttached = false;

    async componentDidMount() {
        await this.captchaStore.init();
        this.loadCaptcha();

        document.addEventListener('mousedown', this.onClickCallback);
        this.captchaListenerAttached = true;
    }

    async componentDidUpdate(lastprop: CaptchaProps) {
        if (this.props.forceShow === lastprop.forceShow) {
            return;
        }

        if (this.captchaListenerAttached) {
            this.captchaListenerAttached = false;
            document.removeEventListener('mousedown', this.onClickCallback);
        }

        const grecaptchaElement = document.getElementById('grecaptchaElement');
        if (grecaptchaElement) {
            const newgrecaptchaElement = document.createElement('div');
            newgrecaptchaElement.setAttribute('id', 'grecaptchaElement');
            newgrecaptchaElement.setAttribute('class', Styles.grecaptcha);

            grecaptchaElement.replaceWith(newgrecaptchaElement);
        }

        this.removeChild(this.script);
        this.captchaStore.isCaptchaLoaded = false;
        this.removeChild(this.getCaptchaIframe());

        await this.loadCaptcha();
    }

    componentWillUnmount() {
        this.removeChild(this.script);
        this.captchaStore.isCaptchaLoaded = false;
        this.removeChild(this.getCaptchaIframe());

        if (this.captchaListenerAttached) {
            this.captchaListenerAttached = false;
            document.removeEventListener('mousedown', this.onClickCallback);
        }
    }

    private onClickCallback = () => {
        if (!this.props.onError) {
            return;
        }

        const captchamodal = this.getCaptchaIframe();

        const parentElement = captchamodal?.parentNode?.parentElement;
        if (parentElement?.style.opacity !== '0') {
            this.props.onError('Captcha verification failed.');
        }
    };

    private getCaptchaIframe = () =>
        document.querySelector("iframe[title*='recaptcha']") as HTMLIFrameElement;

    private removeChild = (node?: Node) => node?.parentNode?.removeChild(node);

    loadCaptcha = async () => {
        const { isCaptchaLoaded, isCaptchaLoading } = this.captchaStore;
        if (isCaptchaLoaded) {
            this.renderCaptcha();
            throw false;
        }
        if (isCaptchaLoading) {
            throw false;
        }
        this.captchaStore.toggleLoadingState();
        return new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src =
                'https://www.google.com/recaptcha/api.js?onload=onCaptchaLoad&render=explicit';
            (window as any).onCaptchaLoad = () => {
                this.captchaStore.hasLoaded();
                this.captchaStore.toggleLoadingState();
                delete (window as any).onCaptchaLoad;
                this.renderCaptcha();
                resolve();
            };
            script.onerror = () => {
                this.captchaStore.toggleLoadingState();
                if (this.props.onError !== undefined) {
                    this.props.onError('Captcha verification failed.');
                }
                reject();
            };
            document.body.appendChild(script);
            if (this.props.onLoaded !== undefined) {
                this.props.onLoaded();
            }

            this.script = script;
        });
    };

    renderCaptcha = () => {
        grecaptcha.render('grecaptchaElement', {
            'sitekey': this.captchaStore.siteKey!,
            'callback': this.props.callback ?? this.verifyCaptcha,
            'size': this.props.forceShow ? 'normal' : 'invisible',
            'expired-callback': this.expireCaptcha,
        });
    };

    verifyCaptcha = async (token: string) => {
        if (!this.props.forceShow) {
            await this.props.onVerified?.();
            return;
        }
        try {
            const result = await axios.post('/app/api/Auth/CheckCaptcha', {
                gRecaptchaResponse: token,
                isInvisibleCaptcha: !this.props.forceShow,
                ...(this.props.params || {}),
            });

            if (result.data.Error) {
                throw result.data.Error;
            }
            this.captchaStore.triggerSuccessfulVerification();
        } catch (e) {
            if (this.props.onError !== undefined) {
                this.props.onError('Captcha verification failed.');
            }
            throw e;
        }

        if (this.props.onVerified !== undefined) {
            await this.props.onVerified();
        }
    };

    expireCaptcha = () => {
        const { resetVerificationStatus } = this.captchaStore;
        resetVerificationStatus();
    };

    render() {
        return (
            <Fragment>
                <div id="grecaptchaElement" className={Styles.grecaptcha} />
                <Text subdued size="1" className={classNames('m-x-1-i')}>
                    This site is protected by reCAPTCHA and the Google&nbsp;
                    <Link
                        primary
                        rel="noopener noreferrer"
                        target="_blank"
                        href="https://policies.google.com/privacy"
                    >
                        Privacy Policy
                    </Link>
                    &nbsp;and&nbsp;
                    <Link
                        primary
                        rel="noopener noreferrer"
                        target="_blank"
                        href="https://policies.google.com/terms"
                    >
                        Terms of Service
                    </Link>
                </Text>
            </Fragment>
        );
    }
}
